import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import {
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionTrigger,
  AccordionContent,
  Alert,
  Button,
  Modal,
  ModalActions,
  ModalContent,
  ModalTitleInverse,
  useSnackbar,
} from '@c2fo/liquidity';
import { computeMarketSubmitStats } from '@/enterprise/components/OfferBuilder/utils';
import { OfferConfigDetails } from '@/enterprise/components/OfferConfigDetails';
import { OfferReviewDetails } from '@/enterprise/components/OfferReviewDetails';
import Skeleton from '@/enterprise/components/Skeleton';
import { useConfirmAgreementsWhenRequired } from '@/enterprise/data/useAgreements';
import useSubmitBenchmarkOffer from '@/enterprise/data/useSubmitBenchmarkOffer';
import useSubmitPriceDiscoveryOffer, {
  SubmitPriceDiscoveryOfferInput,
} from '@/enterprise/data/useSubmitPriceDiscoveryOffer';
import useSubmitStaticOffer from '@/enterprise/data/useSubmitStaticOffer';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useReporting } from '@/reporting';
import useDataDog from '@/reporting/DataDog';
import { useIntercom } from '@/reporting/Intercom';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import useExchangeRates from '@/shared/data/useExchangeRates';
import getTakerMarketName from '@/utils/getTakerMarketName';
import localeRateToDomain from '@/utils/localeRateToDomain';
import { trimDateTimestamp } from '@/utils/trimDateTimestamp';
import useLocaleFormat from '@/utils/useLocaleFormat';
import { getNormalizedRate } from '@/utils/validateRate';
import { Agreement } from '../agreements/Agreement';
import { getRateFromTakerMarket } from '../nameYourRate/utils';
import { useOfferDiscountAPRDisplay } from '../takerMarketTable/utils/useOfferDiscountAPRDisplay';
import useVariableRateDetails from '../variableRate/useVariableRateDetails';
import { BenchmarkRateInput } from './inputs/BenchmarkRateInput';
import FrequencyInput from './inputs/FrequencyInput';
import { PriceDiscoveryRateInput } from './inputs/PriceDiscoveryRateInput';
import { StaticRateInput } from './inputs/StaticRateInput';
import { OfferModalForm } from './types';
import {
  buildCadenceFromExistingOfferSchedule,
  buildCustomScheduleSentenceFromFormData,
  checkIfDurationIsATypicalOngoingOffer,
  getDefaultRateType,
  validateCadence,
  validateRateInputForTakerMarket,
} from './utils';

export type OfferModalAccordionValue = 'Rate' | 'Schedule' | 'Frequency' | '';
export type OfferModalReferrer = 'offer' | 'participation' | undefined;

interface OfferModalProps {
  takerMarket: TakerMarket;
  open: boolean;
  onClose: () => void;
  initialStep?: 'entry' | 'review';
  initialAccordionValue?: OfferModalAccordionValue;
  referrer?: OfferModalReferrer;
}

const OfferModalComponent = ({
  accordionValue,
  initialStep,
  onClose,
  open,
  setAccordionValue,
  takerMarket,
  referrer = 'offer',
}: Omit<OfferModalProps, 'initialAccordionValue'> & {
  accordionValue: OfferModalAccordionValue;
  setAccordionValue: (value: OfferModalAccordionValue) => void;
}) => {
  const { t } = useTranslation();
  const { asCurrency } = useLocaleFormat();
  const { data: exchangeRates } = useExchangeRates();
  const [formStep, setFormStep] = useState<'entry' | 'review'>(initialStep ?? 'entry');
  const { mutateAsync: submitPriceDiscoveryOffer } = useSubmitPriceDiscoveryOffer();
  const { mutateAsync: submitBenchmarkOffer } = useSubmitBenchmarkOffer();
  const { mutateAsync: submitStaticOffer } = useSubmitStaticOffer();
  const existingOfferSchedule = takerMarket.offerSchedule;
  const showSnackbar = useSnackbar();
  const { validateAndConfirmAgreementsConditionally } = useConfirmAgreementsWhenRequired([takerMarket]);
  const { getVariableRateDetails } = useVariableRateDetails();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { trackEnterpriseEvent } = useReporting();
  const { addError } = useDataDog();
  const { triggerNPSSurvey } = useIntercom();
  const [submitOfferError, setSubmitOfferError] = useState<string | null>(null);
  const { offerDiscountAPRDisplay } = useOfferDiscountAPRDisplay();
  const getDefaultFormValues = useCallback(() => {
    const rateFromTakerMarket = getRateFromTakerMarket(takerMarket);

    const cadence = buildCadenceFromExistingOfferSchedule(existingOfferSchedule?.schedule);

    // Guard against empty strings (Platform API can return empty strings currently)
    const startDate = !!existingOfferSchedule?.startDate ? existingOfferSchedule?.startDate : undefined;

    // Guard against empty strings (Platform API can return empty strings currently)
    let endDate = !!existingOfferSchedule?.endDate ? existingOfferSchedule?.endDate : undefined;

    // If an old offer expiration date is present and there is no offer schedule end date yet, use it as the default end date
    // This will serve as a sort of "migration" for a while, hopefully we can delete this in the future.
    if (!endDate && takerMarket.disableAfterMarketClearsDate) {
      endDate = trimDateTimestamp(takerMarket.disableAfterMarketClearsDate);
    }

    const frequency = takerMarket.offerConfig.frequency === 'ONE_TIME' ? 'one-time' : 'recurring';

    // Enable schedule only if there's already an offer schedule
    const enableSchedule =
      frequency === 'recurring' && !checkIfDurationIsATypicalOngoingOffer({ startDate, endDate, cadence });

    return {
      rateStr: rateFromTakerMarket > 0 ? `${rateFromTakerMarket}` : '',
      rateTypeStr: getDefaultRateType(takerMarket),
      isAgreementsChecked: false,
      frequency,
      enableSchedule,
      cadence,
      // Clear schedule fields when frequency is one-time
      startDate: frequency === 'one-time' ? undefined : startDate,
      endDate: frequency === 'one-time' ? undefined : endDate,
    } satisfies OfferModalForm;
  }, [existingOfferSchedule, takerMarket]);

  const form = useForm<OfferModalForm>({
    defaultValues: getDefaultFormValues(),
  });

  const { reset, handleSubmit, formState, watch, setValue, setError, clearErrors, trigger, getValues, setFocus } = form;

  const resetForm = useCallback(() => {
    reset(getDefaultFormValues());
    setFormStep(initialStep ?? 'entry');
    setIsSubmitting(false);
  }, [getDefaultFormValues, initialStep, reset]);

  const [formRateStr, formRateTypeStr, startDate, endDate, cadence, frequency, enableSchedule] = watch([
    'rateStr',
    'rateTypeStr',
    'startDate',
    'endDate',
    'cadence',
    'frequency',
    'enableSchedule',
  ]);

  const hasSetSchedule =
    frequency !== 'one-time' && !checkIfDurationIsATypicalOngoingOffer({ startDate, endDate, cadence });

  const rateDisplay = useMemo(() => {
    if (takerMarket.marketPricingType === 'BENCHMARK') {
      const { rateLabel } = getVariableRateDetails(takerMarket);
      return rateLabel;
    }

    const domainRate = localeRateToDomain(formRateStr);

    if (isNaN(parseFloat(formRateStr)) || !domainRate) {
      return t('taker.setARate');
    }

    if (formRateTypeStr !== 'apr' && formRateTypeStr !== 'disc') {
      return t('taker.setARate');
    }

    const formRateTypeStrDisplay = formRateTypeStr === 'disc' ? t('core.discount') : t('core.apr');
    return offerDiscountAPRDisplay(getNormalizedRate(domainRate), formRateTypeStrDisplay);
  }, [formRateStr, formRateTypeStr, getVariableRateDetails, takerMarket, t, offerDiscountAPRDisplay]);

  const getMarketSubmitStats = useCallback(
    (rateOverride?: number, rateTypeOverride?: 'APR' | 'DISC') => {
      const domainRate = localeRateToDomain(formRateStr);
      const rate = rateOverride ?? (domainRate ? getNormalizedRate(domainRate) : 0);
      const rateType = rateTypeOverride ?? (formRateTypeStr === 'disc' ? 'DISC' : 'APR');

      return computeMarketSubmitStats({
        takerMarket,
        rate,
        rateType,
        exchangeRates,
      });
    },
    [formRateStr, formRateTypeStr, exchangeRates, takerMarket]
  );

  const onNext: SubmitHandler<OfferModalForm> = async (data) => {
    if (formStep === 'entry') {
      // validate a rate is set before sending to review step
      const { errorMessage: rateErrorMessage } = validateRateInputForTakerMarket({
        rateInput: getValues('rateStr'),
        rateType: getValues('rateTypeStr'),
        takerMarket,
        t,
      });

      if (rateErrorMessage) {
        setError('rateStr', { message: rateErrorMessage });
        setAccordionValue('Rate');
        setTimeout(() => {
          setFocus('rateStr');
        }, 300); // match accordion animation duration

        return;
      }

      if (data.frequency === 'recurring') {
        const { errorMessage: cadenceErrorMessage } = validateCadence(cadence, t);

        if (cadenceErrorMessage) {
          setError('cadence.value', { message: cadenceErrorMessage });
          setAccordionValue('Frequency');
          setTimeout(() => {
            setFocus('cadence');
          }, 300); // match accordion animation duration

          return;
        }
      }

      return setFormStep('review');
    }

    setIsSubmitting(true);
    setSubmitOfferError(null);

    const onOfferSubmitError = (error: Error, type: 'offer' | 'schedule') => {
      const errorMessage =
        type === 'schedule' ? t('offerSubmitDialog.scheduleSetError') : t('offerSubmitDialog.offerSetError');
      showSnackbar({
        message: errorMessage,
      });
      setSubmitOfferError(errorMessage);
      setIsSubmitting(false);
      addError('Enterprise Offer Submit Error', {
        error,
        location: 'OfferModal component -> onOfferSubmitError handler',
        takerMarket,
        formData: data,
      });
    };

    const onOfferSubmitSuccess = () => {
      onClose();
      resetForm();
      showSnackbar({ message: t('offerSubmitDialog.offerSet') });
      triggerNPSSurvey();
    };

    /**
     * These are the offer types this component supports.
     * If the market pricing type is not one of these, we need to return an error before doing anything else.
     */
    if (!['PRICE_DISCOVERY', 'BENCHMARK', 'STATIC'].includes(takerMarket.marketPricingType ?? '')) {
      return onOfferSubmitError(new Error('Unknown market pricing type'), 'offer');
    }

    try {
      // This will check if agreements are required or not.
      // If required, it will ensure the user has checked the checkbox and make mutation calls.
      // If not required, it will exit early and allow this method to proceed.
      await validateAndConfirmAgreementsConditionally({ isAgreementsChecked: data.isAgreementsChecked });
    } catch {
      setIsSubmitting(false);
      setError('isAgreementsChecked', { message: 'Agreements must be accepted' });
      return;
    }

    // for one-time offers, always set schedule to null
    // for recurring offers, check if schedule is enabled and if typical ongoing offer
    const hasNoSchedule =
      data.frequency === 'one-time' ||
      !data.enableSchedule ||
      checkIfDurationIsATypicalOngoingOffer({
        startDate: data.startDate,
        endDate: data.endDate,
        cadence: data.cadence,
      });

    const schedule = hasNoSchedule
      ? null
      : {
          startDate: data.startDate,
          endDate: data.endDate,
          daysOfWeek: data.cadence.value,
        };

    // Convert locale rate to domain rate to normalize. i.e. 10,7 becomes 10.7.
    const normalizedRate = getNormalizedRate(localeRateToDomain(formRateStr)!);
    const maxDiscount = formRateTypeStr === 'disc' ? normalizedRate : takerMarket.offerConfig.maxDiscount;
    const maxApr = formRateTypeStr === 'apr' ? normalizedRate : takerMarket.offerConfig.maxApr;

    if (takerMarket.marketPricingType === 'PRICE_DISCOVERY') {
      try {
        const submitValues = {
          marketUuid: takerMarket.offerConfig.marketUuid,
          offerConfig: {
            uuid: takerMarket.offerConfig.uuid,
            isDiscountBidding: formRateTypeStr === 'disc',
            maxDiscount,
            maxApr,
            exclusionSettings: takerMarket.offerConfig.exclusionSettings,
            // We will default schedule end date to any existing offer expiration date, therefore set this to null in favor of schedule.
            expireOn: null,
            schedule,
            frequency: data.frequency === 'one-time' ? 'ONE_TIME' : 'RECURRING',
          },
          supplierDivisionUuid: takerMarket.takerDivisionUuid,
        } satisfies SubmitPriceDiscoveryOfferInput;
        await submitPriceDiscoveryOffer(submitValues);
        trackEnterpriseEvent(`${referrer}::submitted`, {
          ...submitValues,
          ...getMarketSubmitStats(),
          rate: Math.round(normalizedRate * 100) / 100,
          rateType: formRateTypeStr === 'disc' ? 'DISC' : 'APR',
          referrer: 'offer-modal',
          isDraft: false,
          isEdit: !!(takerMarket.offerConfig.uuid && takerMarket.offerConfig.isEnabled),
          isEnabled: true,
          isManuallyAwarded: takerMarket.offerConfig.isManuallyAwarded,
          isPreferred: takerMarket.offerConfig.offerType === 'PREFERRED_TERM',
        });
      } catch (error) {
        return onOfferSubmitError(error as Error, 'offer');
      }
    }

    if (takerMarket.marketPricingType === 'BENCHMARK') {
      try {
        const submitValues = {
          marketUuid: takerMarket.offerConfig.marketUuid,
          supplierDivisionUuid: takerMarket.takerDivisionUuid,
          // We will default schedule end date to any existing offer expiration date, therefore set this to null in favor of schedule.
          expireOn: null,
          schedule,
          frequency: data.frequency,
        };

        const variableRateDetails = getVariableRateDetails(takerMarket);

        const benchmarkRate = variableRateDetails?.estimatedRate ?? 0;
        await submitBenchmarkOffer(submitValues);
        trackEnterpriseEvent(`${referrer}::variable-rate::submitted`, {
          ...submitValues,
          ...getMarketSubmitStats(benchmarkRate, 'APR'),
          rate: Math.round(benchmarkRate * 100) / 100,
          rateType: 'APR',
          referrer: 'offer-modal',
          isDraft: false,
          isEdit: !!(takerMarket.offerConfig.uuid && takerMarket.offerConfig.isEnabled),
          isEnabled: true,
          isManuallyAwarded: takerMarket.offerConfig.isManuallyAwarded,
          isPreferred: takerMarket.offerConfig.offerType === 'PREFERRED_TERM',
        });
      } catch (error) {
        return onOfferSubmitError(error as Error, 'offer');
      }
    }

    if (takerMarket.marketPricingType === 'STATIC') {
      try {
        const submitValues = {
          rate: normalizedRate,
          marketUuid: takerMarket.marketUuid,
          supplierDivisionUuid: takerMarket.takerDivisionUuid,
          // We will default schedule end date to any existing offer expiration date, therefore set this to null in favor of schedule.
          expireOn: null,
          schedule,
          frequency: data.frequency,
        };
        await submitStaticOffer(submitValues);
        trackEnterpriseEvent(`${referrer}::fixed-rate::submitted`, {
          ...submitValues,
          ...getMarketSubmitStats(normalizedRate, 'APR'),
          rate: Math.round(normalizedRate * 100) / 100,
          rateType: 'APR',
          referrer: 'offer-modal',
          isDraft: false,
          isEdit: !!(takerMarket.offerConfig.uuid && takerMarket.offerConfig.isEnabled),
          isEnabled: true,
          isManuallyAwarded: takerMarket.offerConfig.isManuallyAwarded,
          isPreferred: takerMarket.offerConfig.offerType === 'PREFERRED_TERM',
        });
      } catch (error) {
        return onOfferSubmitError(error as Error, 'offer');
      }
    }

    if (schedule) {
      trackEnterpriseEvent('offer::schedule::success', schedule);
    }

    return onOfferSubmitSuccess();
  };

  const onBack = () => {
    clearErrors();
    setFormStep('entry');
  };

  const accordionTriggerClick = async (e: React.MouseEvent<HTMLButtonElement>, value: OfferModalAccordionValue) => {
    e.preventDefault();
    e.stopPropagation();

    const isValid = await trigger();

    if (value === 'Schedule' && accordionValue !== 'Schedule') {
      trackEnterpriseEvent('offer::schedule::opened');
    }

    if (value === 'Frequency' && accordionValue !== 'Frequency') {
      trackEnterpriseEvent('offer::frequency::opened');
    }

    if (isValid) {
      setAccordionValue(value === accordionValue ? '' : value);
    } else {
      // When result of trigger is invalid, calling 'handleSubmit' (even thought we know it will fail) ensures that form errors are handled as expected.
      // 1. Form errors continue to re-eval on input change
      // 2. Focus state moves to the first invalid field
      // See issue thread for more details: https://github.com/react-hook-form/react-hook-form/issues/3425
      handleSubmit(onNext)();
    }
  };

  // Reset the form when the modal open state changes
  useEffect(() => {
    resetForm();
  }, [open, resetForm]);

  useEffect(() => {
    clearErrors('rateStr');
    clearErrors('cadence');
  }, [clearErrors, formRateTypeStr]);

  const formHasErrors = Object.keys(formState.errors).length > 0;
  const cadenceError = formState.errors?.cadence?.value?.message;

  const entryStep = (
    <Accordion
      type="single"
      collapsible
      className="overflow-hidden rounded-md"
      defaultValue="Rate"
      value={accordionValue}
    >
      {/* Available AR */}
      <AccordionItem divided value="Available AR">
        <AccordionHeader className="flex-auto justify-between bg-canvas pr-14">
          <div>{t('core.includedAmount')}</div>
          <div className="font-semibold">{asCurrency(takerMarket.eligibleInvoiceAmount, takerMarket.currency)}</div>
        </AccordionHeader>
      </AccordionItem>
      {/* Offer Rate */}
      <AccordionItem divided value="Rate">
        <AccordionHeader className="bg-canvas">
          <div className="flex flex-auto justify-between">
            {takerMarket.marketPricingType === 'PRICE_DISCOVERY' && <div>{t('core.rate')}</div>}
            {takerMarket.marketPricingType === 'BENCHMARK' && <div>{t('core.variableRate')}</div>}
            {takerMarket.marketPricingType === 'STATIC' && <div>{t('core.fixedRate')}</div>}
            <div className="font-semibold">{rateDisplay}</div>
          </div>
          <AccordionTrigger
            name="Rate Accordion Toggle"
            onClick={(e) => accordionTriggerClick(e, 'Rate')}
            disabled={formHasErrors}
          />
        </AccordionHeader>
        <AccordionContent className="bg-blueGray-50 pb-0">
          {takerMarket.marketPricingType === 'PRICE_DISCOVERY' && <PriceDiscoveryRateInput takerMarket={takerMarket} />}
          {takerMarket.marketPricingType === 'BENCHMARK' && <BenchmarkRateInput takerMarket={takerMarket} />}
          {takerMarket.marketPricingType === 'STATIC' && <StaticRateInput takerMarket={takerMarket} />}
        </AccordionContent>
      </AccordionItem>
      <AccordionItem divided value="Frequency">
        <AccordionHeader className="bg-canvas">
          <div className="flex flex-auto justify-between">
            <div>{t('builder.frequency')}</div>
            <div className="font-semibold">
              {frequency === 'one-time' ? t('builder.oneTime') : t('builder.recurring')}
            </div>
          </div>
          <AccordionTrigger
            name="Frequency Accordion Toggle"
            onClick={(e) => accordionTriggerClick(e, 'Frequency')}
            disabled={formHasErrors}
          />
        </AccordionHeader>
        <AccordionContent className="bg-blueGray-50 pb-0">
          <FrequencyInput
            takerMarkets={[takerMarket]}
            schedule={{ startDate, endDate, cadence }}
            setStartDate={(date) => setValue('startDate', date)}
            setEndDate={(date) => setValue('endDate', date)}
            setCadence={(cadence) => {
              setValue('cadence', cadence);
              clearErrors('cadence');
            }}
            errors={cadenceError ? { cadence: { message: cadenceError } } : {}}
          />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );

  const reviewStep = (
    <div className="space-y-6">
      <OfferConfigDetails
        items={[
          {
            label: t('core.includedAmount'),
            value: asCurrency(takerMarket.eligibleInvoiceAmount, takerMarket.currency),
          },
          {
            label: t('taker.dictionary.offer.label'),
            value: rateDisplay,
          },
          {
            label: t('builder.frequency'),
            value: frequency === 'one-time' ? t('builder.oneTime') : t('builder.recurring'),
          },
          ...(frequency === 'recurring' && enableSchedule
            ? [
                {
                  label: t('scheduling.schedule'),
                  value: buildCustomScheduleSentenceFromFormData({
                    startDate: watch('startDate'),
                    endDate: watch('endDate'),
                    cadence: watch('cadence'),
                    t,
                  }),
                },
              ]
            : []),
          // If market is a DSF market, add Funded By Bank Partner information row
          ...(takerMarket.marketIsDSF
            ? [
                {
                  label: t('taker.history.fundedBy'),
                  value: t('taker.history.bankPartner'),
                  tooltip: t('taker.history.fundedByBankPartnerTooltip'),
                },
              ]
            : []),
        ]}
      />

      <div className="px-2">
        <OfferReviewDetails takerMarket={takerMarket} hasSetSchedule={hasSetSchedule} />
      </div>

      <div className="px-2">
        <Agreement
          takersMarkets={[takerMarket]}
          onChange={(v) => {
            setValue('isAgreementsChecked', v);
            clearErrors('isAgreementsChecked');
          }}
        />
        {!!formState.errors.isAgreementsChecked && (
          <div className="pt-3">
            <Alert type="error" variant="filled" description={t('agreements.error.checkbox')} full />
          </div>
        )}
        {!!submitOfferError && (
          <div className="pt-3">
            <Alert type="error" variant="filled" description={submitOfferError} full />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <ModalContent className="space-y-4">
        {takerMarket.eligibleInvoiceAmount <= 0 && (
          <Alert
            variant="outlined"
            type="warning"
            full
            title={t('offerSubmitDialog.zeroApWarningTitle')}
            description={t('offerSubmitDialog.zeroApWarningDescription')}
          />
        )}
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onNext)}>
            {formStep === 'entry' && entryStep}
            {formStep === 'review' && reviewStep}
          </form>
        </FormProvider>
      </ModalContent>
      <ModalActions>
        {formStep === 'entry' && (
          <>
            <Button variant="secondary" onClick={() => onClose()}>
              {t('core.cancel')}
            </Button>
            <Button variant="primary" onClick={handleSubmit(onNext)} className="sm:min-w-[100px]">
              {t('core.next')}
            </Button>
          </>
        )}

        {formStep === 'review' && (
          <>
            <Button variant="secondary" onClick={onBack} disabled={isSubmitting}>
              {t('core.back')}
            </Button>
            <Button
              variant="cta"
              onClick={handleSubmit(onNext)}
              className="sm:min-w-[100px]"
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              {t('core.submit')}
            </Button>
          </>
        )}
      </ModalActions>
    </>
  );
};

const OfferModal = ({ initialAccordionValue, ...props }: OfferModalProps) => {
  const { t } = useTranslation();
  const [accordionValue, setAccordionValue] = useState<OfferModalAccordionValue>('Rate');

  // controlling the accordion value here in the parent ensures
  // the modal content opens with the correct accordion expanded
  useLayoutEffect(() => {
    if (initialAccordionValue) {
      setAccordionValue(initialAccordionValue);
    }
  }, [initialAccordionValue]);

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      // TODO: If we want controlled heights, TBD with design.
      // sm:[@media(min-height:300px)]:h-[95%] sm:[@media(min-height:700px)]:h-[85%] sm:[@media(min-height:900px)]:h-[75%]
      className="sm:w-[750px]"
    >
      <ModalTitleInverse>
        <span className="flex flex-col">
          <span>{t('core.setOfferLabel')}</span>
          <span className="text-sm font-normal text-text-secondary">{getTakerMarketName(props.takerMarket)}</span>
        </span>
      </ModalTitleInverse>
      <QueryBoundaries
        LoadingComponent={() => (
          <div className="m-6 space-y-4 rounded-md bg-canvas p-4">
            <div className="space-y-2">
              <Skeleton className="h-6 w-1/3 opacity-70" shadow={false} />
              <Skeleton className="h-10 w-full opacity-60" shadow={false} />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-6 w-1/4 opacity-70" shadow={false} />
              <Skeleton className="h-10 w-full opacity-60" shadow={false} />
            </div>
            <div className="space-y-2 pt-2">
              <Skeleton className="h-6 w-1/2 opacity-70" shadow={false} />
              <Skeleton className="h-24 w-full opacity-60" shadow={false} />
            </div>
          </div>
        )}
      >
        <OfferModalComponent {...props} accordionValue={accordionValue} setAccordionValue={setAccordionValue} />
      </QueryBoundaries>
    </Modal>
  );
};

export default OfferModal;
